import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import "./MultiSelect.css";

const MultiSelect = ({ options, value, onChange, placeholder }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOptions, setSelectedOptions] = useState(value || []);
    const [selectAllChecked, setSelectAllChecked] = useState(false);

    // Only update selectedOptions if value is different
    useEffect(() => {
        const isDifferent =
            value?.length !== selectedOptions.length ||
            (value && selectedOptions.some((opt, i) => opt.value !== value[i]?.value));
        if (isDifferent) {
            setSelectedOptions(value || []);
        }
    }, [value]);



    // Filter options based on search term
    const filteredOptions = options?.filter((option) =>
        option?.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Handle checkbox change for individual options
    const handleCheckboxChange = (option, isChecked) => {
        let newSelected;
        if (isChecked) {
            newSelected = [...selectedOptions, option];
        } else {
            newSelected = selectedOptions.filter((selected) => selected.value !== option.value);
        }
        setSelectedOptions(newSelected);
        onChange(newSelected);
    };

    // Handle "Select All" checkbox
    const handleSelectAll = (isChecked) => {
        setSelectAllChecked(isChecked);
        const newSelected = isChecked ? filteredOptions : [];
        setSelectedOptions(newSelected);
        onChange(newSelected);
    };



    // Automatically check 'Select All' if all filteredOptions are selected (by value)
    useEffect(() => {
        // Check if all filteredOptions are selected (by value)
        const allSelected =
            filteredOptions.length > 0 &&
            filteredOptions.length === selectedOptions.length &&
            filteredOptions.every((fo) => selectedOptions.some((so) => so.value === fo.value));
        setSelectAllChecked(allSelected);
    }, [filteredOptions, selectedOptions]);

    // Reset selections when search term changes
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);

    };

    return (
        <div className="dropdown-container w-100 border rounded-1" style={{ minWidth: "100px" }}>
            <div className="dropdown-header" data-bs-toggle="dropdown" aria-expanded="false">
                {placeholder && <span>{placeholder}</span>}
                <span>
                    <span translate="no" className="px-1"> {selectedOptions.length}</span> selected</span>
            </div>
            <div className="dropdown-content dropdown-menu p-0 w-100">
                <input
                    type="text"
                    className="search-input form-control form-control-sm"
                    placeholder="Search options..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
                {filteredOptions.length > 0 && (
                    <div className="option-item d-flex align-items-center">
                        <label>   <input
                            type="checkbox"
                            id="select-all-checkbox"
                            checked={selectAllChecked}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="form-check-input me-2 ft-9"
                        />
                            <span className="form-check-label mb-0 ft-9" >Select All</span></label>
                    </div>
                )}
                {filteredOptions.map((option) => (
                    <div key={option.value} className="option-item d-flex align-items-center">
                        <button
                            type="button"
                            aria-pressed={selectedOptions.some(selected => selected.value === option.value)}
                            onClick={(event) => {
                                event.stopPropagation();
                                const isChecked = selectedOptions.some(selected => selected.value === option.value);
                                handleCheckboxChange(option, !isChecked);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    const isChecked = selectedOptions.some(selected => selected.value === option.value);
                                    handleCheckboxChange(option, !isChecked);
                                }
                            }}
                            style={{ cursor: 'pointer', width: '100%', background: 'none', border: 'none', padding: 0, textAlign: 'left' }}
                        >
                            <input
                                type="checkbox"
                                id={`option-checkbox-${option.value}`}
                                checked={selectedOptions.some((selected) => selected.value === option.value)}
                                onChange={(e) => {
                                    handleCheckboxChange(option, e.target.checked);
                                }}
                                className="form-check-input me-2 ft-9"
                                onClick={e => e.stopPropagation()} // Prevent dropdown from closing when clicking checkbox
                            />
                            <span className="form-check-label text-break mb-0 ft-9">{option.label}</span>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

MultiSelect.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    value: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
};

export default MultiSelect;